/**
 * Pure decision logic for the email-verification gate (ENG-3797).
 *
 * Extracted from the React component so every branch the spec mandates can be
 * unit-tested without a renderer — the frontend package has no `renderHook`
 * dependency (see auth/protect.tsx for the same extraction pattern).
 *
 * The truth source is the Auth0 `email_verified` OIDC claim, NOT the registry
 * `emailVerified` field. Per spec-fragment.md §3 GO/NO-GO: the registry value
 * never flips to true for database (email/password) users and would mass-lock
 * the existing DB user base — so the gate reads the Auth0 claim, which reflects
 * real verification state for every user on both browser and Electron.
 */

import { isElectronApp } from '#~/electron/utils.js';

/** Which app shell the gate is rendering in (analytics dimension, spec §analytics). */
export type VerificationSurface = 'browser' | 'electron';

/** Tri-state of the resolved `email_verified` claim (spec §4). */
export type VerificationSignal = 'verified' | 'unverified' | 'unknown';

/** What the gate should render this frame. */
export type GateOutcome = 'allow' | 'pending' | 'resolving' | 'error';

/** Why the gate reached its decision — analytics only, never carries raw email. */
export type DecisionReason = 'unverified' | 'sso_bypass' | 'flag_off' | 'status_unknown';

/** The subset of Auth0 OIDC claims the gate needs. */
export interface VerificationClaims {
  /** `email_verified` claim, or undefined when not yet loaded. */
  emailVerified: boolean | undefined;
  /** `sub` claim (used for the SSO/IdP check, spec §5). */
  sub: string | undefined;
  /** `email` claim (display + change-email only; never sent to analytics). */
  email: string | undefined;
}

/**
 * Auth0 sets `sub` to `google-oauth2|<id>` for the only non-password IdP wired
 * in the backend (spec §5; matches the backend `IsProviderID()` contract). The
 * bare prefix with no suffix is NOT an SSO subject (auth0_test.go:260).
 */
const SSO_SUBJECT_PREFIX = 'google-oauth2|';

/** Narrowing predicate (a type guard, not a `as` assertion). */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Safely extract the verification claims from the (untyped in Electron) Auth0
 * user object. Returns `undefined` for any field whose runtime type is wrong,
 * so a malformed `/userinfo` payload degrades to "unknown" (fail-closed) rather
 * than throwing or being coerced into a false "verified".
 */
export const readClaims = (user: unknown): VerificationClaims => {
  const record = isRecord(user) ? user : undefined;

  const emailVerified = record?.email_verified;
  const sub = record?.sub;
  const email = record?.email;

  return {
    emailVerified: typeof emailVerified === 'boolean' ? emailVerified : undefined,
    sub: typeof sub === 'string' ? sub : undefined,
    email: typeof email === 'string' ? email : undefined
  };
};

/**
 * SSO identification by IdP/connection, matching the backend's
 * `IsProviderID()` contract (spec §5). SSO users carry `email_verified: true`
 * from Auth0 anyway, so this is a robustness/telemetry aid, not the sole allow
 * path — but it guarantees an SSO user is never gated even if the claim is
 * momentarily unread.
 */
export const isSsoSubject = (sub: string | undefined): boolean =>
  typeof sub === 'string' &&
  sub.startsWith(SSO_SUBJECT_PREFIX) &&
  sub.length > SSO_SUBJECT_PREFIX.length;

/**
 * Map the claims to the tri-state signal (spec §4). `undefined` email_verified
 * means the claim has not resolved yet → "unknown" (drives the bounded loader),
 * NOT an implicit allow.
 */
export const resolveVerificationSignal = (
  claims: Pick<VerificationClaims, 'emailVerified' | 'sub'>
): VerificationSignal => {
  // SSO bypass (spec §5) takes precedence — these users are always verified.
  if (isSsoSubject(claims.sub)) {
    return 'verified';
  }

  if (claims.emailVerified === true) {
    return 'verified';
  }

  if (claims.emailVerified === false) {
    return 'unverified';
  }

  return 'unknown';
};

/**
 * The gate blocks ONLY when the flag resolves to exactly `true`. While the flag
 * is loading (`undefined`) or errored (`defaultValue: false`) the gate is fully
 * transparent (spec §6 boot-time safety: "PostHog unreachable/slow → gate OFF").
 * This is the single most important regression to protect — default-OFF.
 */
export const shouldGate = (flagEnabled: boolean | undefined): boolean => flagEnabled === true;

/**
 * Auth0 silent-auth outcomes that are EXPECTED while a user is still unverified
 * and are NOT a connection failure (ENG-3797 Failure 1). A silent
 * `getAccessTokenSilently({ cacheMode: 'off' })` rejects with one of these when
 * it simply cannot complete yet — no usable SSO session (`login_required`),
 * consent/MFA/interaction still pending, or a transient `timeout`. None of these
 * mean the network is down, so the gate must keep the pending UI and keep
 * polling; only a SUSTAINED run of genuine transport failures reaches the
 * terminal connection-error screen.
 */
const RECOVERABLE_REFRESH_ERROR_CODES = new Set<string>([
  'login_required',
  'consent_required',
  'interaction_required',
  'mfa_required',
  'account_selection_required',
  'timeout'
]);

/** Auth0 /userinfo HTTP statuses that mean "try again", not "you're offline". */
const RECOVERABLE_USERINFO_STATUSES = new Set<number>([401, 429]);

const userinfoStatusFromError = (error: unknown): number | undefined => {
  const record = isRecord(error) ? error : undefined;
  if (record !== undefined && typeof record.status === 'number') {
    return record.status;
  }
  const message =
    error instanceof Error
      ? error.message
      : record !== undefined && typeof record.message === 'string'
        ? record.message
        : undefined;
  if (message === undefined) {
    return undefined;
  }
  const match = /^userinfo (\d{3})$/.exec(message);
  if (match === null) {
    return undefined;
  }
  return Number(match[1]);
};

/** Whether a rejected silent refresh is an expected/transient outcome or a genuine connection failure. */
export type RefreshFailureKind = 'recoverable' | 'connection';

/**
 * Classify a rejected silent-refresh error (ENG-3797 Failure 1). auth0-spa-js
 * throws errors carrying an `error` code string (e.g. `login_required`,
 * `timeout`); a bare network `Error` or any unrecognized shape is treated as a
 * genuine connection failure so a real outage still surfaces after the bound.
 * Pure so the classification is unit-testable without a renderer.
 */
export const classifyRefreshFailure = (error: unknown): RefreshFailureKind => {
  const code = isRecord(error) && typeof error.error === 'string' ? error.error : undefined;
  if (code !== undefined && RECOVERABLE_REFRESH_ERROR_CODES.has(code)) {
    return 'recoverable';
  }
  // 401/429 on the live /userinfo poll: Auth0 rate-limit or stale bearer while
  // the user is still unverified. Counting these as connection failures flipped
  // the pending screen to "Check your connection" (ENG-3797 Didier / slot F).
  const status = userinfoStatusFromError(error);
  if (status !== undefined && RECOVERABLE_USERINFO_STATUSES.has(status)) {
    return 'recoverable';
  }
  return 'connection';
};

/**
 * The core render decision. Fail-CLOSED on unknown (spec §4): an unresolved
 * signal yields "resolving" (bounded loader) and then "error", never "allow".
 *
 * ENG-4602: the server-derived `verificationRequired` signal (a tri-state:
 * true | false | undefined) is the grandfather gate. Enforcement happens ONLY
 * for an account the server marks `true` (unverified in our DB with no prior
 * product use). Every other value — `false` (verified or grandfathered legacy)
 * or `undefined` (absent field / still loading / older server that predates the
 * field) — fails OPEN before the claim signal is consulted, so a legacy account
 * with an unreadable claim never reaches the connection-error dead-end.
 */
export const decideGate = (input: {
  flagEnabled: boolean | undefined;
  signal: VerificationSignal;
  /**
   * Server-derived gate signal (ENG-4602). `true` gates; `false`/`undefined`
   * fail open. Tri-state so an absent field / older server never coerces to a
   * blocking value.
   */
  verificationRequired: boolean | undefined;
  /** True once the bounded resolving window has elapsed still-unknown. */
  resolveExhausted: boolean;
  /**
   * True once polling has SUSTAINED-failed — i.e. a bounded run of consecutive
   * genuine connection failures (ENG-3797 Failure 1). A single ordinary silent-
   * auth rejection (login_required/consent_required/timeout) no longer sets this,
   * so the gate does not flip to the error screen before the user verifies.
   */
  pollFailed: boolean;
}): GateOutcome => {
  // Flag off / loading / errored → transparent passthrough.
  if (!shouldGate(input.flagEnabled)) {
    return 'allow';
  }

  // Verified is MONOTONIC and wins over everything below (ENG-3797 flicker fix,
  // codex round-1 finding 2). This is checked BEFORE pollFailed because a poll
  // rejection from a stale in-flight request can land AFTER the user has been
  // observed verified (the verify auto-dismiss already happened); without this
  // ordering that late failure would flip an already-verified user to the
  // terminal error screen — a post-verify reversion the gate must never do. A
  // genuinely verified email does not un-verify, so allowing here is correct;
  // the signal carries the session verified latch, so it stays verified across
  // remounts/out-of-order responses too. The unverified/unknown connection-
  // failure path below still surfaces the error screen as before.
  if (input.signal === 'verified') {
    return 'allow';
  }

  // Per rule: absent-data-gates-permissively (ai.knowledge/global) — the
  // server-derived signal gates ONLY on an explicit `true`. `false`
  // (grandfathered legacy / verified) and `undefined` (absent field / loading /
  // older server) both fail OPEN here, BEFORE the claim branch below. This is
  // what stops a grandfathered account with an unreadable claim from reaching
  // the terminal VerificationError screen (ENG-4602). Only the new-unverified-
  // signup cohort (server `true`) falls through to the claim-driven paths.
  if (input.verificationRequired !== true) {
    return 'allow';
  }

  // A failed poll is terminal for a still-unverified/unknown user — surface an
  // explicit retry rather than spinning or guessing (spec §4).
  if (input.pollFailed) {
    return 'error';
  }

  if (input.signal === 'unverified') {
    return 'pending';
  }

  // signal === 'unknown': bounded loader, then explicit error. Never allow.
  return input.resolveExhausted ? 'error' : 'resolving';
};

/**
 * Boundary actions an unverified account must verify before completing.
 * Opening the app, adding a camera, going live, and changing email are never
 * gated. New-email activation needs no entry: the registry clears verification
 * on an email change, so a new address cannot drive these until verified.
 */
export type GatedAction = 'password_reset' | 'invite' | 'billing';

/** What a gated action should do right now. */
export type ActionGateOutcome = 'allow' | 'verify';

/**
 * Per-action verification decision. Ignores `verificationRequired` on purpose:
 * that field grandfathers legacy accounts at the front door, while boundary
 * actions apply to every unverified account.
 *
 * Asks only when the flag is exactly `true`, the claim is explicitly
 * `unverified` (SSO and the verified latch resolve to `verified`), and the
 * registry does not report the address verified. The registry value only ever
 * allows, so a DB-verified account with a stale Auth0 claim is never asked.
 *
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) — unknown claim or flag allows.
 */
export const decideActionGate = (input: {
  flagEnabled: boolean | undefined;
  signal: VerificationSignal;
  registryEmailVerified: boolean | null | undefined;
}): ActionGateOutcome => {
  if (!shouldGate(input.flagEnabled)) {
    return 'allow';
  }

  if (input.signal !== 'unverified') {
    return 'allow';
  }

  if (input.registryEmailVerified === true) {
    return 'allow';
  }

  return 'verify';
};

/**
 * Remaining client cooldown in ms. Pure so the 60s resend gate (spec §7) is
 * testable without timers. Returns 0 when never sent or already elapsed.
 */
export const remainingCooldownMs = (
  lastSentAt: number | undefined,
  now: number,
  cooldownMs: number
): number => {
  if (lastSentAt === undefined) {
    return 0;
  }

  const remaining = cooldownMs - (now - lastSentAt);
  return remaining > 0 ? remaining : 0;
};

/** Resolve the analytics surface dimension from the runtime (spec §analytics). */
export const getSurface = (): VerificationSurface => (isElectronApp() ? 'electron' : 'browser');
